import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowDownIcon, ArrowUpIcon, CancelCrossIcon, Sheet } from "@activecollab/components";
import PropTypes from "prop-types";
import ErrorBoundary from "~/components/ErrorBoundry";
import { useObjectSheetContext } from "~/components/Sheet/ObjectSheetContext";
import { useNoteNavigation } from "~/repository";
import { useHistoryContext } from "~/routes/context";
import { getUpdateHideNotifications } from "~/store/selectors/selector.configOptions";
import { getNoteById } from "~/store/selectors/selector.notes";
import Angie from "~/utils/angie";
import { usePageTitle, useUnreadCount } from "../Hooks";
import { Note } from "../Note/Note";
import { NoteTrashSlate } from "../Note/NoteTrashSlate";
import { VersionProvider } from "../Note/VersionContext";

export const NoteSheet = React.forwardRef(({ id, projectId, open, onClose }, ref) => {
  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const { t } = useTranslation();
  const note = useSelector((state) => getNoteById(state, id));
  const { state: historyState } = useHistoryContext();
  const prevPageTitle = historyState[historyState.length - 1]?.title;
  const [edit, setEdit] = useState(false);
  const [versionId, setVersionId] = useState(0);
  const [modificationId, setModificationId] = useState(null);

  const updatesHideNotifications = useSelector(getUpdateHideNotifications);
  const totalUnreadUpdates = useUnreadCount();

  usePageTitle(open ? note?.name : prevPageTitle, !updatesHideNotifications ? totalUnreadUpdates : 0);

  const { closeSheet } = useObjectSheetContext();
  const handleClose = () => {
    setEdit(false);
    setVersionId(0);
    setModificationId(null);
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const inListContext =
    new RegExp(`/projects/${projectId}/notes$`).test(pathname) ||
    new RegExp(`/projects/${projectId}/notebooks/${note?.notebook_id}$`).test(pathname);
  const {
    data = {},
    isLoading,
    isFetched,
  } = useNoteNavigation(note?.project_id, id, {
    retry: 0,
    enabled: note?.project_id > 0,
  });
  const { next, prev } = data;

  const handlePrev = useCallback(() => {
    if (prev) {
      Angie.logger.info("Note navigation button clicked.", {
        direction: "prev",
        navigation_source: "sheet",
        navigation_type: "note",
      });
      navigate(`${pathname}?modal=Note-${prev}-${projectId}`);
    }
  }, [navigate, pathname, prev, projectId]);

  const handleNext = useCallback(() => {
    if (next) {
      Angie.logger.info("Note navigation button clicked.", {
        direction: "next",
        navigation_source: "sheet",
        navigation_type: "note",
      });
      navigate(`${pathname}?modal=Note-${next}-${projectId}`);
    }
  }, [navigate, next, pathname, projectId]);

  const loading = !isFetched || isLoading;

  const showPrevNext = note && note.project_id === projectId && Boolean(prev || next);

  const controls = useMemo(
    () =>
      showPrevNext && inListContext && !loading && !note?.is_trashed && !edit && versionId === 0
        ? [
            {
              tooltip: t("Close"),
              onClick: closeSheet,
              icon: <CancelCrossIcon />,
              className: "task_modal_close_icon tw-order-1 sm:tw-order-1",
            },
            {
              tooltip: next ? t("Next Note") : t("You have reached the end of the list"),
              onClick: handleNext,
              icon: <ArrowDownIcon />,
              disabled: !next,
              className: "tw-order-2 sm:tw-order-3",
            },
            {
              tooltip: prev ? t("Previous Note") : t("You are at beginning of the list"),
              onClick: handlePrev,
              icon: <ArrowUpIcon />,
              disabled: !prev,
              className: "tw-order-3 sm:tw-order-2",
            },
          ]
        : [
            {
              tooltip: t("Close"),
              onClick: closeSheet,
              icon: <CancelCrossIcon />,
              className: "task_modal_close_icon",
            },
          ],
    [
      closeSheet,
      edit,
      handleNext,
      handlePrev,
      inListContext,
      loading,
      next,
      note?.is_trashed,
      prev,
      showPrevNext,
      t,
      versionId,
    ]
  );

  useEffect(() => {
    setEdit(state?.edit);
  }, [state]);

  const handleCloseForm = useCallback(() => {
    navigate(`${pathname}?modal=Note-${id}-${projectId}`, {
      replace: true,
    });
  }, [id, navigate, pathname, projectId]);

  return (
    <VersionProvider
      value={{
        versionId,
        setVersionId,
        modificationId,
        setModificationId,
      }}
    >
      <Sheet
        ref={ref}
        open={open}
        position="right"
        animation="right"
        mode="stretch"
        onClose={handleClose}
        controls={controls}
        className="task-modal-container"
      >
        <ErrorBoundary errorLocation="Note Sheet">
          <NoteTrashSlate
            id={id}
            projectId={projectId}
          >
            <Note
              id={id}
              projectId={projectId}
              edit={edit}
              setEdit={setEdit}
              onCloseForm={handleCloseForm}
              shouldOverflow
            />
          </NoteTrashSlate>
        </ErrorBoundary>
      </Sheet>
    </VersionProvider>
  );
});

NoteSheet.propTypes = {
  id: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

NoteSheet.displayName = "NoteSheet";
